export const ACCESS_GROUPS = {
  Admin: "DXP_Opslink_Admin",
  InternalUsers: "DXP_Opslink_InternalUsers",
  ExternalUsers: "DXP_Opslink_ExternalUsers",
  OpsSupport: "DXP_Opslink_RoleOpsSupport",
  VesselPerformance: "DXP_Opslink_RoleVesselPerformance",
} as const;

export type AccessGroup = (typeof ACCESS_GROUPS)[keyof typeof ACCESS_GROUPS];

export interface AccessGroupRule {
  anyOf?: readonly AccessGroup[];
  allOf?: readonly AccessGroup[];
  noneOf?: readonly AccessGroup[];
}

export const APP_AUDIENCE_GROUPS = [ACCESS_GROUPS.InternalUsers, ACCESS_GROUPS.ExternalUsers] as const;

export const hasGroupName = (groups: readonly string[] | undefined, group: string): boolean =>
  groups?.includes(group) ?? false;

export const hasAccessGroup = (groups: readonly string[] | undefined, group: AccessGroup): boolean =>
  hasGroupName(groups, group);

export const hasAnyAccessGroup = (
  groups: readonly string[] | undefined,
  requiredGroups: readonly AccessGroup[],
): boolean => requiredGroups.some((group) => hasAccessGroup(groups, group));

export const hasAppAudience = (groups: readonly string[] | undefined): boolean =>
  hasAnyAccessGroup(groups, APP_AUDIENCE_GROUPS);

const isAudienceGroup = (group: AccessGroup): boolean =>
  APP_AUDIENCE_GROUPS.includes(group as (typeof APP_AUDIENCE_GROUPS)[number]);

const matchesAnyOfRule = (
  groups: readonly string[] | undefined,
  anyOf: readonly AccessGroup[] | undefined,
): boolean => {
  if (!anyOf?.length) {
    return true;
  }

  if (hasAnyAccessGroup(groups, anyOf)) {
    return true;
  }

  return hasAccessGroup(groups, ACCESS_GROUPS.Admin) && anyOf.some((group) => !isAudienceGroup(group));
};

export const matchesAccessGroupRule = (
  groups: readonly string[] | undefined,
  rule: AccessGroupRule | undefined,
): boolean => {
  if (!rule) {
    return true;
  }

  if (!matchesAnyOfRule(groups, rule.anyOf)) {
    return false;
  }

  if (rule.allOf?.length && !rule.allOf.every((group) => hasAccessGroup(groups, group))) {
    return false;
  }

  if (rule.noneOf?.length && rule.noneOf.some((group) => hasAccessGroup(groups, group))) {
    return false;
  }

  return true;
};
